import './index.css'

import lodashUniqueId from 'lodash/uniqueId'
import * as React from 'react'
import CopyToClipboard from 'react-clipboard.js'

import { pushUINotification } from '@disco/utils/notifications'

import { UINotification } from 'types/notification'

import { Box, Flex } from 'components/ds'
import Button from 'components/ui/button'
import TextField from 'components/ui/text-field'
import UITooltip from 'components/ui/tooltip'

type Props = {
  after?: React.ReactNode
  nodeRight?: React.ReactNode
  compactClipboardInput?: boolean
  customButton?: React.ReactNode
  disabled?: boolean
  label?: React.ReactNode
  onCopy?: (...args: Array<any>) => any
  readOnly?: boolean
  value?: string
  withoutIcon?: boolean
  tooltipLabel?: string
  successNotification?: UINotification
  customCta?: React.ReactNode
}
type OwnState = {
  uniqueId: string
  isClicked: boolean
}
/**
 * Input component with integrated copy-to-clipboard functionality
 * Supports multiple display modes: compact, without icon, and custom CTA
 */
export default class ClipboardInput extends React.Component<Props, OwnState> {
  state: OwnState = {
    uniqueId: lodashUniqueId(),
    isClicked: false,
  }
  _onSuccess: (e: { clearSelection: any }) => void = (e: {
    clearSelection: (...args: Array<any>) => any
  }) => {
    e.clearSelection()
    this.setState({
      isClicked: true,
    })

    if (this.props.onCopy) {
      this.props.onCopy()
    }

    setTimeout(() => {
      if (this)
        this.setState({
          isClicked: false,
        })
    }, 1000)
  }
  _renderButton: (
    clipboardInputId: string,
    button: React.ReactNode,
  ) => React.ReactElement<'div'> = (
    clipboardInputId: string,
    button: React.ReactNode,
  ) => {
    return (
      <div
        id={`ClipboardButton-${this.state.uniqueId}`}
        className="ClipboardButton"
        title="Copy to clipboard"
      >
        <CopyToClipboard
          component="div"
          data-clipboard-target={`#${clipboardInputId}`}
          onSuccess={this._onSuccess}
        >
          {button}
        </CopyToClipboard>
      </div>
    )
  }

  render(): React.ReactElement<'div'> | React.ReactNode {
    const value = this.props.value || ''
    const clipboardInputId = `ClipboardInput-${this.state.uniqueId}`
    const notification: UINotification = this.props.successNotification || {
      message: 'Copied!',
      level: 'success',
    }
    const button = (
      <Button
        appearance="transparent"
        disabled={this.props.disabled}
        icon={this.state.isClicked ? 'check' : 'link'}
        tooltip="Copy"
      />
    )
    const isMac = navigator.platform.includes('Mac')

    if (this.props.customCta) {
      return (
        <div className="ClipboardInput_Compact">
          <CopyToClipboard
            data-clipboard-target={`#${clipboardInputId}`}
            data-clipboard-text={value}
            onSuccess={() => pushUINotification(notification)}
            onError={() =>
              pushUINotification({
                message: `Press ${isMac ? 'Cmd' : 'Ctrl'}+C to copy`,
                level: 'error',
              })
            }
          >
            <div id={clipboardInputId}>{this.props.customCta}</div>
          </CopyToClipboard>
        </div>
      )
    }

    if (this.props.withoutIcon) {
      return (
        <UITooltip content={this.props.tooltipLabel} position="top">
          <div className="ClipboardInput_Compact">
            <CopyToClipboard
              data-clipboard-target={`#${clipboardInputId}`}
              data-clipboard-text={value}
              onSuccess={() => pushUINotification(notification)}
              onError={() =>
                pushUINotification({
                  message: `Press ${isMac ? 'Cmd' : 'Ctrl'}+C to copy`,
                  level: 'error',
                })
              }
            >
              <div id={clipboardInputId}>
                <span className="ClipboardInput_Label">
                  {this.props.label} {value}
                </span>
              </div>
            </CopyToClipboard>
          </div>
        </UITooltip>
      )
    }

    if (this.props.compactClipboardInput) {
      return (
        <div className="ClipboardInput_Compact">
          <CopyToClipboard
            data-clipboard-target={`#${clipboardInputId}`}
            onSuccess={this._onSuccess}
          >
            <React.Fragment>
              <div>
                <TextField
                  nodeRight={this.props.nodeRight}
                  disabled={this.props.disabled}
                  id={clipboardInputId}
                  label={this.props.label}
                  readOnly
                  value={value}
                />
              </div>
              {button}
              {this.props.after && <div>{this.props.after}</div>}
            </React.Fragment>
          </CopyToClipboard>
        </div>
      )
    } else {
      return (
        <Flex gap="2" align="end">
          <Box flexGrow="1">
            <TextField
              nodeRight={this.props.nodeRight}
              id={clipboardInputId}
              label={this.props.label}
              readOnly={this.props.readOnly}
              value={value}
              disabled={this.props.disabled}
            />
          </Box>
          {this.props.customButton ||
            this._renderButton(clipboardInputId, button)}
          {this.props.after && <div>{this.props.after}</div>}
        </Flex>
      )
    }
  }
}
